import { type FinanceCurrency } from "@/features/finance/model/finance-types"
import { type EmploymentType } from "@/features/members/model/members-types"

export enum TenantLocale {
  En = "en",
  Fr = "fr",
  Rw = "rw",
}
export enum DateFormatPreference {
  DdMmYyyy = "DD/MM/YYYY",
  MmDdYyyy = "MM/DD/YYYY",
  YyyyMmDd = "YYYY-MM-DD",
}
export enum Weekday {
  Monday = "monday",
  Tuesday = "tuesday",
  Wednesday = "wednesday",
  Thursday = "thursday",
  Friday = "friday",
  Saturday = "saturday",
  Sunday = "sunday",
}
export type TenantGeneralSettings = {
  logoUrl: string
  legalName: string
  displayName: string
  primaryContactEmail: string
  supportPhone: string
  headquartersAddress: string
}

export type TenantRegionalSettings = {
  defaultLocale: TenantLocale
  defaultTimezone: string
  baseCurrency: FinanceCurrency
  allowedCurrencies: FinanceCurrency[]
  exchangeRates: Partial<Record<FinanceCurrency, number>>
  dateFormat: DateFormatPreference
}

export type TenantCalendarSettings = {
  workWeekStart: Weekday
  workingDays: Record<Weekday, boolean>
  workdayStart: string
  workdayEnd: string
  publicHolidaysNote: string
}

export type TenantOperationalSettings = {
  employeeIdPrefix: string
  fiscalYearStartMonth: number
  approvalSlaHours: number
  activityRetentionMonths: number
}

export type TenantPeopleSettings = {
  teams: string[]
  defaultTeam: string
  allowedEmploymentTypes: EmploymentType[]
  defaultEmploymentType: EmploymentType
  inviteExpiryDays: number
}

export type TenantMembershipSettings = {
  planTier: TenantPlanTier
  membershipStatus: TenantMembershipStatus
  billingCycle: TenantBillingCycle
  seatLimit: number
  billingEmail: string
  autoRenew: boolean
  contractStartDate: string
  contractEndDate: string
  accountManager: string
  supportTier: TenantSupportTier
}

export enum TenantPlanTier {
  Starter = "starter",
  Growth = "growth",
  Business = "business",
  Enterprise = "enterprise",
}
export enum TenantMembershipStatus {
  Trial = "trial",
  Active = "active",
  PastDue = "past_due",
  Suspended = "suspended",
}
export enum TenantBillingCycle {
  Monthly = "monthly",
  Annual = "annual",
}
export enum TenantSupportTier {
  Standard = "standard",
  Priority = "priority",
  Dedicated = "dedicated",
}
export type TenantComplianceSettings = {
  dataProcessingContact: string
  privacyPolicyUrl: string
  termsUrl: string
  incidentContactEmail: string
}

export type TenantSettingsRecord = {
  general: TenantGeneralSettings
  regional: TenantRegionalSettings
  calendar: TenantCalendarSettings
  operational: TenantOperationalSettings
  people: TenantPeopleSettings
  // Transitional projection; authoritative subscription data lives in components/subscription.
  membership: TenantMembershipSettings
  compliance: TenantComplianceSettings
  updatedAt: string
  updatedBy: string
}

export type TenantSettingsInput = Omit<TenantSettingsRecord, "updatedAt" | "updatedBy">

export type TenantSettingsValidationErrors = Partial<Record<string, string>>
